import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Industries", path: "/industries" },
  { label: "Case Studies", path: "/case-studies" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // When scrolled: use theme-aware bg + text colors
  // When transparent (top of page): always white text so it reads over the dark hero image
  const isTransparent = !scrolled;

  const scrolledBg = isDark
    ? "bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10"
    : "bg-white/95 backdrop-blur-xl border-b border-black/8";

  const mobileBg = isDark
    ? "bg-[#0a0a0a]/98 backdrop-blur-xl border-t border-white/10"
    : "bg-white/98 backdrop-blur-xl border-t border-black/8";

  // Link color logic
  const getLinkColor = (isActive: boolean) => {
    if (isTransparent) {
      return isActive ? "text-white" : "text-white/65 hover:text-white";
    }
    if (isDark) {
      return isActive ? "text-white" : "text-white/55 hover:text-white";
    }
    return isActive ? "text-[#1d1d1f]" : "text-[#1d1d1f]/55 hover:text-[#1d1d1f]";
  };

  const iconColor = isTransparent
    ? "text-white/75"
    : isDark
    ? "text-white/70"
    : "text-[#1d1d1f]/70";

  const iconBg = isTransparent
    ? "bg-white/12 hover:bg-white/22"
    : isDark
    ? "bg-white/8 hover:bg-white/15"
    : "bg-black/6 hover:bg-black/12";

  const ctaBg = isTransparent
    ? "bg-white text-[#0a0a0a] hover:bg-white/90"
    : isDark
    ? "bg-white text-[#0a0a0a] hover:bg-white/90"
    : "bg-[#1d1d1f] text-white hover:bg-[#1d1d1f]/85";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? scrolledBg : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-[68px]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/5799cc79-ebf2-42bb-b3ff-edc4746d4641.png"
            alt="Complete AI Business Solutions"
            className="h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-[13px] font-medium tracking-wide transition-colors duration-200 whitespace-nowrap ${getLinkColor(
                location.pathname === link.path
              )}`}
            >
              {link.label}
            </Link>
          ))}

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${iconBg} ${iconColor}`}
          >
            <i className={`text-base ${isDark ? "ri-sun-line" : "ri-moon-line"}`}></i>
          </button>

          <Link
            to="/contact"
            className={`ml-1 px-5 py-2 text-[13px] font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${ctaBg}`}
          >
            Book a Call
          </Link>
        </nav>

        {/* Mobile right */}
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${iconColor}`}
          >
            <i className={`text-lg ${isDark ? "ri-sun-line" : "ri-moon-line"}`}></i>
          </button>
          <button
            className={`w-9 h-9 flex items-center justify-center cursor-pointer ${
              isTransparent ? "text-white" : isDark ? "text-white" : "text-[#1d1d1f]"
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`md:hidden ${mobileBg} px-6 py-6 flex flex-col gap-5`}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-base font-medium tracking-wide transition-colors duration-200 ${
                isDark
                  ? location.pathname === link.path ? "text-white" : "text-white/55"
                  : location.pathname === link.path ? "text-[#1d1d1f]" : "text-[#1d1d1f]/55"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className={`mt-2 px-5 py-3 text-sm font-semibold rounded-full text-center whitespace-nowrap cursor-pointer ${
              isDark ? "bg-white text-[#0a0a0a]" : "bg-[#1d1d1f] text-white"
            }`}
          >
            Book a Call
          </Link>
        </div>
      )}
    </header>
  );
}
